import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { ReactComponent as ArrowIcon } from "assets/icons/left-arrow.svg";
import { getPromoConfig } from "constants/payments";
import "./pay.css";

const MIN_PAYMENT = 1000;

const Payment = ({ mora, user, setUser, setFormConfig }) => {
  const location = useLocation();
  const promo = getPromoConfig(location);

  const [selectedOption, setSelectedOption] = useState(null);

  const formatterMoney = useMemo(
    () =>
      new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    [],
  );

  const valorTotal = Math.max(0, Number(mora?.pago_total ?? user?.saldo ?? 0));
  const pagoAlDia = Math.max(0, Number(mora?.pago_al_dia ?? 0));
  const cuotaSugerida = Math.max(0, Number(mora?.valor_cuota_mes ?? 0));
  const tieneDescuento = Boolean(promo);
  const discountPercent = Number(promo?.percent ?? 0);

  const valorConDescuento = promo
    ? Math.round(valorTotal * Number(promo.factor ?? 1))
    : valorTotal;

  const valorInicial =
    user?.tipo === "Reservas"
      ? 0
      : tieneDescuento
        ? valorConDescuento
        : valorTotal;

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      tipo: user?.tipo ?? "",
      contrato: user?.contrato ?? "",
      codigo: user?.codigo ?? "",
      valor: valorInicial,
    },
  });

  const getValueForOption = (option) => {
    switch (option) {
      case "al_dia":
        return pagoAlDia;

      case "cuota_sugerida":
        return cuotaSugerida;

      case "descuento":
        return valorConDescuento;

      case "saldo":
      default:
        return valorTotal;
    }
  };

  const handleOptionChange = (option) => {
    const newValue = getValueForOption(option);

    setSelectedOption(option);

    setValue("valor", newValue, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  useEffect(() => {
    const initialOption =
      user?.tipo === "Reservas"
        ? "saldo"
        : tieneDescuento && valorTotal > 0
          ? "descuento"
          : "saldo";

    setSelectedOption(initialOption);

    setValue("valor", getValueForOption(initialOption), {
      shouldValidate: true,
    });
  }, [
    user?.tipo,
    tieneDescuento,
    valorTotal,
    valorConDescuento,
    pagoAlDia,
    cuotaSugerida,
    setValue,
  ]);

  const onSubmit = handleSubmit(async (data) => {
    const monto = Number(data.valor);

    if (!Number.isFinite(monto) || monto < MIN_PAYMENT) {
      return;
    }

    setUser({
      ...user,
      monto,
    });

    setFormConfig({ step: 4 });
  });

  return (
    <main>
      <form onSubmit={onSubmit}>
        <article
          className="text-back"
          onClick={() => setFormConfig({ step: 2 })}
        >
          <ArrowIcon />
          Volver
        </article>

        <label htmlFor="concepto">Concepto</label>

        <input
          className="inputPay"
          type="text"
          id="concepto"
          readOnly
          {...register("tipo")}
        />

        <label htmlFor="contrato">Contrato</label>

        <input
          className="inputPay"
          type="text"
          id="contrato"
          readOnly
          {...register("contrato")}
        />

        {user?.tipo !== "Reservas" && (
          <>
            {user?.mora === "1" && mora?.fecha_mora && (
              <p className="text-danger">
                Tu plan vacacional presenta mora desde: {mora.fecha_mora}
              </p>
            )}

            <div className="radio-inputs mt-4 mb-3">
              {pagoAlDia > 0 && (
                <label>
                  <input
                    className="radio-input"
                    type="radio"
                    name="paymentOption"
                    checked={selectedOption === "al_dia"}
                    onChange={() => handleOptionChange("al_dia")}
                  />

                  <span className="radio-tile">
                    <span className="radio-label">
                      Pago para estar al día: {formatterMoney.format(pagoAlDia)}
                    </span>
                  </span>
                </label>
              )}

              {cuotaSugerida > 0 && (
                <label>
                  <input
                    className="radio-input"
                    type="radio"
                    name="paymentOption"
                    checked={selectedOption === "cuota_sugerida"}
                    onChange={() => handleOptionChange("cuota_sugerida")}
                  />

                  <span className="radio-tile">
                    <span className="radio-label">
                      Cuota sugerida: {formatterMoney.format(cuotaSugerida)}
                    </span>
                  </span>
                </label>
              )}

              {valorTotal > 0 && (
                <label>
                  <input
                    className="radio-input"
                    type="radio"
                    name="paymentOption"
                    checked={selectedOption === "saldo"}
                    onChange={() => handleOptionChange("saldo")}
                  />

                  <span className="radio-tile">
                    <span className="radio-label">
                      Pagar saldo: {formatterMoney.format(valorTotal)}
                    </span>
                  </span>
                </label>
              )}
            </div>

            {tieneDescuento && valorTotal > 0 && (
              <button
                type="button"
                className={`discount-box ${
                  selectedOption === "descuento" ? "selected" : ""
                }`}
                onClick={() => handleOptionChange("descuento")}
              >
                <span className="discount-badge">{discountPercent}% OFF</span>

                <p className="discount-original">
                  {formatterMoney.format(valorTotal)}
                </p>

                <p className="discount-final">
                  {formatterMoney.format(valorConDescuento)}
                </p>

                <p className="discount-label">
                  <strong>🎉 TENEMOS UN BENEFICIO ESPECIAL PARA TI! 🎉</strong>
                  <br />
                  Paga hoy la totalidad del saldo de tu Plan Vacacional y
                  disfruta de un {discountPercent}% de descuento sobre el saldo
                  de tu plan. ¡No dejes pasar esta oportunidad de ahorrar!
                </p>
              </button>
            )}
          </>
        )}

        <div className={errors?.valor ? "ErrorContainer" : ""}>
          <label htmlFor="valor">Valor</label>

          <Controller
            name="valor"
            control={control}
            rules={{
              required: "Debe digitar un valor a pagar",

              validate: (value) => {
                const numericValue = Number(value);

                if (!Number.isFinite(numericValue)) {
                  return "Debe digitar un valor válido";
                }

                if (numericValue < MIN_PAYMENT) {
                  return "El valor mínimo debe ser de $1.000";
                }

                return true;
              },
            }}
            render={({ field }) => (
              <input
                className="inputPay"
                type="text"
                id="valor"
                inputMode="numeric"
                autoComplete="off"
                placeholder="$ 0"
                value={field.value ? formatterMoney.format(field.value) : ""}
                onChange={(event) => {
                  const numericValue = event.target.value.replace(/\D/g, "");
                  const parsedValue = numericValue ? Number(numericValue) : "";
                  field.onChange(parsedValue);
                  setSelectedOption(null);
                }}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
              />
            )}
          />

          {errors?.valor && (
            <span className="errorForm">
              <i className="fa-solid fa-circle-exclamation"></i>{" "}
              {errors.valor.message}
            </span>
          )}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="MercadoPago mt-3"
        >
          {isSubmitting ? (
            <span className="spinner-border spinner-border-sm"></span>
          ) : (
            "Continuar"
          )}
        </button>
      </form>
    </main>
  );
};

export default Payment;
